/**
 * The languages the menu offers, in ONE place.
 *
 * History: this list lived in `hooks/menu/useMenuTranslation.ts`, a `"use
 * client"` module. The AI prompt builder (`lib/ai/prompts/language.ts`) imported
 * it from there so the offered list and the prompt could not drift apart — but a
 * server route importing a named export from a client module is a Next.js
 * boundary violation, and Turbopack mangles the export in that context
 * (`LANGUAGES.find is not a function`), which 500'd every chat and voice turn.
 *
 * The list is plain data with no React, no browser globals and no `"use client"`
 * directive, so both the client hook and the server prompt code can import it.
 * The hook re-exports it so its public API is unchanged.
 */

export const ORIGINAL_LANGUAGE = "original";

/** True when no translation should be requested. Treats an empty/unknown value
 *  as "original" so a stale stored code can never strand the guest. */
export function isOriginalLanguage(code: string | null | undefined): boolean {
  return !code || code === ORIGINAL_LANGUAGE;
}

export const LANGUAGES = [
  { code: "en", label: "English", native: "English", flag: "🇬🇧" },
  { code: "tw", label: "Twi", native: "Twi", flag: "🇬🇭" },
  { code: "gaa", label: "Ga", native: "Ga", flag: "🇬🇭" },
  { code: "ee", label: "Ewe", native: "Eʋegbe", flag: "🇬🇭" },
  { code: "ha", label: "Hausa", native: "Hausa", flag: "🇳🇬" },
  { code: "yo", label: "Yoruba", native: "Yorùbá", flag: "🇳🇬" },
  { code: "fr", label: "French", native: "Français", flag: "🇫🇷" },
  { code: "es", label: "Spanish", native: "Español", flag: "🇪🇸" },
  { code: "de", label: "German", native: "Deutsch", flag: "🇩🇪" },
  { code: "it", label: "Italian", native: "Italiano", flag: "🇮🇹" },
  { code: "pt", label: "Portuguese", native: "Português", flag: "🇵🇹" },
  { code: "ja", label: "Japanese", native: "日本語", flag: "🇯🇵" },
  { code: "zh", label: "Chinese (Simplified)", native: "简体中文", flag: "🇨🇳" },
  { code: "ar", label: "Arabic", native: "العربية", flag: "🇸🇦" },
  { code: "nl", label: "Dutch", native: "Nederlands", flag: "🇳🇱" },
] as const;